import { ChangeDetectionStrategy, Component, DestroyRef, NgZone, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { PlatformSystemStatusDto } from '@data/_generated-api/platform-api/models/platform-system-status-dto';
import { PlatformSystemStatusService } from '@data/_generated-api/platform-api/services/platform-system-status.service';
import { firstValueFrom, interval } from 'rxjs';
import {
  HEALTHCHECKS_DASHBOARD_URL,
  UPTIME_ROBOT_DASHBOARD_URL,
  formatDurationSeconds,
  healthTone,
  healthchecksTone,
  monitorTone,
  platformTone,
  uptimeRobotTone
} from './system-status.utils';

/** De api cachet 60 s; vaker vernieuwen levert niets nieuws op. */
const AUTO_REFRESH_MS = 60_000;

/**
 * Statuspaneel in het beheer (docs/BEHEER.md): UptimeRobot, de nachtelijke back-up in
 * Healthchecks.io en de eigen health checks in één oogopslag. De api haalt alles op met
 * read-only sleutels die de server nooit verlaten; dit paneel toont enkel de statussen.
 */
@Component({
  selector: 'system-status-panel',
  imports: [TranslocoModule, SharedModule],
  templateUrl: './system-status-panel.component.html',
  styleUrl: './system-status-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SystemStatusPanelComponent implements OnInit {
  private readonly systemStatusApi = inject(PlatformSystemStatusService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  protected readonly status = signal<PlatformSystemStatusDto | undefined>(undefined);
  protected readonly isLoading = signal(false);
  protected readonly loadFailed = signal(false);

  protected readonly uptimeRobotTone = computed(() => uptimeRobotTone(this.status()?.uptimeRobot));
  protected readonly healthchecksTone = computed(() => healthchecksTone(this.status()?.healthchecks));
  protected readonly platformTone = computed(() => platformTone(this.status()?.platform));

  protected readonly uptimeRobotDashboardUrl = UPTIME_ROBOT_DASHBOARD_URL;
  protected readonly healthchecksDashboardUrl = HEALTHCHECKS_DASHBOARD_URL;

  protected readonly monitorTone = monitorTone;
  protected readonly healthTone = healthTone;
  protected readonly formatDuration = formatDurationSeconds;

  ngOnInit(): void {
    void this.refresh();
    // Buiten de zone: een lopend interval houdt de app anders nooit "stable".
    this.ngZone.runOutsideAngular(() =>
      interval(AUTO_REFRESH_MS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.ngZone.run(() => void this.refresh()))
    );
  }

  protected async refresh(): Promise<void> {
    if (this.isLoading()) {
      return;
    }
    this.isLoading.set(true);
    try {
      this.status.set(await firstValueFrom(this.systemStatusApi.getSystemstatus()));
      this.loadFailed.set(false);
    } catch {
      this.loadFailed.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }
}
